import type { StorageUnit } from "../emulator.js";

export type PersistedDiskRecord = {
  fileName: string;
  image: Uint8Array;
  unit: StorageUnit;
};

type PersistedDiskRow = {
  fileName: string;
  image: ArrayBuffer;
  unit: number;
};

const DB_NAME = "am8-desktop-web";
const DB_VERSION = 1;
const STORE_NAME = "disk-images";

let openDbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (openDbPromise !== null) {
    return openDbPromise;
  }

  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB is not available in this browser"));
  }

  openDbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => {
      reject(request.error ?? new Error("failed to open IndexedDB"));
    };
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "unit" });
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        openDbPromise = null;
      };
      resolve(db);
    };
  });

  return openDbPromise;
}

async function withStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore, resolve: (value: T) => void, reject: (reason?: unknown) => void) => void
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    transaction.onerror = () => {
      reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    };
    operation(store, resolve, reject);
  });
}

export async function loadPersistedDisk(unit: StorageUnit): Promise<PersistedDiskRecord | null> {
  return withStore<PersistedDiskRecord | null>("readonly", (store, resolve, reject) => {
    const request = store.get(unit);
    request.onerror = () => {
      reject(request.error ?? new Error(`failed to load persisted disk ${unit}`));
    };
    request.onsuccess = () => {
      const row = request.result as PersistedDiskRow | undefined;
      if (row === undefined) {
        resolve(null);
        return;
      }

      resolve({
        fileName: row.fileName,
        image: new Uint8Array(row.image),
        unit: row.unit as StorageUnit
      });
    };
  });
}

export async function savePersistedDisk(record: PersistedDiskRecord): Promise<void> {
  const imageCopy = new Uint8Array(record.image.byteLength);
  imageCopy.set(record.image);

  return withStore<void>("readwrite", (store, resolve, reject) => {
    const request = store.put({
      fileName: record.fileName,
      image: imageCopy.buffer,
      unit: record.unit
    });
    request.onerror = () => {
      reject(request.error ?? new Error(`failed to save persisted disk ${record.unit}`));
    };
    request.onsuccess = () => {
      resolve();
    };
  });
}

export async function deletePersistedDisk(unit: StorageUnit): Promise<void> {
  return withStore<void>("readwrite", (store, resolve, reject) => {
    const request = store.delete(unit);
    request.onerror = () => {
      reject(request.error ?? new Error(`failed to delete persisted disk ${unit}`));
    };
    request.onsuccess = () => {
      resolve();
    };
  });
}
